(function () {
  'use strict';

  var lastDrawerTrigger = null;
  var previousDrawerState = {
    NavDrawer: false,
    CartDrawer: false
  };

  function ready(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  function all(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function matchesSelector(element, selector) {
    if (!element || element.nodeType !== 1) {
      return false;
    }

    var matcher = element.matches || element.msMatchesSelector || element.webkitMatchesSelector;
    return matcher ? matcher.call(element, selector) : false;
  }

  function closest(element, selector) {
    if (!element) {
      return null;
    }

    if (element.closest) {
      return element.closest(selector);
    }

    var node = element.nodeType === 1 ? element : element.parentElement;
    while (node && node.nodeType === 1) {
      if (matchesSelector(node, selector)) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function triggerChange(input) {
    var event;

    if (!input) {
      return;
    }

    if (typeof Event === 'function') {
      event = new Event('change', { bubbles: true });
    } else {
      event = document.createEvent('Event');
      event.initEvent('change', true, true);
    }

    input.dispatchEvent(event);
  }

  function getHeaderOffset() {
    var header = document.querySelector('.site-header--fixed, .site-header');
    var announcementHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--announcement-height'), 10) || 0;
    var headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
    var rect;

    if (header) {
      rect = header.getBoundingClientRect();
      if (rect.bottom > 0) {
        return Math.ceil(rect.bottom) + 16;
      }
    }

    return announcementHeight + headerHeight + 16;
  }

  function ensureFocusNotObscured(element) {
    if (!element || element === document.body || !element.getBoundingClientRect) {
      return;
    }

    window.setTimeout(function () {
      var rect = element.getBoundingClientRect();
      var offset = getHeaderOffset();

      if (rect.top > 0 && rect.top < offset) {
        window.scrollBy(0, rect.top - offset);
      }
    }, 0);
  }

  function focusElement(element) {
    if (!element || typeof element.focus !== 'function') {
      return;
    }

    try {
      element.focus({ preventScroll: true });
    } catch (error) {
      element.focus();
    }

    ensureFocusNotObscured(element);
  }

  function getFirstFocusable(container) {
    var focusables;

    if (!container) {
      return null;
    }

    focusables = all('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container).filter(function (node) {
      return !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true';
    });

    return focusables[0] || null;
  }

  function setDrawerAria(drawerId, isOpen) {
    var drawer = document.getElementById(drawerId);
    if (!drawer) {
      return;
    }

    drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

    if (drawerId === 'CartDrawer') {
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
    }
  }

  function focusDrawer(drawerId) {
    var drawer = document.getElementById(drawerId);
    var target;

    if (!drawer) {
      return;
    }

    target = drawer.querySelector('.js-drawer-close, .drawer__close-button, [data-drawer-close]') || getFirstFocusable(drawer) || drawer;

    if (target === drawer && !drawer.hasAttribute('tabindex')) {
      drawer.setAttribute('tabindex', '-1');
    }

    focusElement(target);
  }

  function setDrawerState() {
    if (!document.body) {
      return;
    }

    var body = document.body;
    var navOpen = body.classList.contains('js-drawer-open-left');
    var cartOpen = body.classList.contains('js-drawer-open-right');

    setDrawerAria('NavDrawer', navOpen);
    setDrawerAria('CartDrawer', cartOpen);

    all('[aria-controls="NavDrawer"][data-drawer]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', navOpen ? 'true' : 'false');
    });

    all('[aria-controls="CartDrawer"][data-drawer]').forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', cartOpen ? 'true' : 'false');
    });

    if (navOpen && !previousDrawerState.NavDrawer) {
      window.setTimeout(function () { focusDrawer('NavDrawer'); }, 50);
    }

    if (cartOpen && !previousDrawerState.CartDrawer) {
      window.setTimeout(function () { focusDrawer('CartDrawer'); }, 50);
    }

    if (!navOpen && previousDrawerState.NavDrawer && lastDrawerTrigger) {
      window.setTimeout(function () { focusElement(lastDrawerTrigger); }, 50);
    }

    if (!cartOpen && previousDrawerState.CartDrawer && lastDrawerTrigger) {
      window.setTimeout(function () { focusElement(lastDrawerTrigger); }, 50);
    }

    previousDrawerState.NavDrawer = navOpen;
    previousDrawerState.CartDrawer = cartOpen;
  }

  function patchDrawerMethods() {
    if (!window.PaloAlto || !window.PaloAlto.Drawers || window.PaloAlto.Drawers._hornetA11yPatched) {
      return;
    }

    var originalOpen = window.PaloAlto.Drawers.open;
    var originalClose = window.PaloAlto.Drawers.close;

    if (typeof originalOpen !== 'function' || typeof originalClose !== 'function') {
      return;
    }

    window.PaloAlto.Drawers.open = function (drawer) {
      if (drawer === 'NavDrawer' || drawer === 'CartDrawer') {
        setDrawerAria(drawer, true);
      }

      var result = originalOpen.apply(this, arguments);
      setDrawerState();
      return result;
    };

    window.PaloAlto.Drawers.close = function () {
      var result = originalClose.apply(this, arguments);
      setDrawerState();
      return result;
    };

    window.PaloAlto.Drawers._hornetA11yPatched = true;
  }

  function watchDrawers() {
    document.addEventListener('click', function (event) {
      var trigger = closest(event.target, '[aria-controls="NavDrawer"][data-drawer], [aria-controls="CartDrawer"][data-drawer]');
      if (trigger) {
        lastDrawerTrigger = trigger;
      }
    }, true);

    patchDrawerMethods();
    setDrawerState();

    if (!document.body || !window.MutationObserver) {
      return;
    }

    var observer = new MutationObserver(setDrawerState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  function panelIdForTab(tab) {
    return tab.getAttribute('aria-controls') || (tab.getAttribute('href') || '').replace('#', '');
  }

  function setTabPanelState(panel, tab, selected) {
    if (!panel) {
      return;
    }

    panel.classList.toggle('tabbed-collections__item--active', selected);
    panel.setAttribute('aria-hidden', selected ? 'false' : 'true');

    if (selected) {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
    } else {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
    }
  }

  function activateTab(tab, setFocus) {
    if (!tab) {
      return;
    }

    var tablist = closest(tab, '[role="tablist"], [data-tabs-nav]');
    if (!tablist) {
      return;
    }

    var tabs = all('[data-tab-button]', tablist);
    var section = closest(tab, '[data-section-type="tabbed-collections"]') || closest(tab, '.tabbed-collections-section');

    tabs.forEach(function (item) {
      var itemPanelId = panelIdForTab(item);
      var itemPanel = itemPanelId ? document.getElementById(itemPanelId) : null;
      var selected = item === tab;

      item.setAttribute('role', 'tab');
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.setAttribute('tabindex', selected ? '0' : '-1');
      item.classList.toggle('tabbed-collections__nav__button--active', selected);
      setTabPanelState(itemPanel, item, selected);
    });

    if (section) {
      var controlledPanels = tabs.map(panelIdForTab);
      all('[data-tab-item]', section).forEach(function (panel) {
        if (controlledPanels.indexOf(panel.id) === -1) {
          panel.setAttribute('aria-hidden', 'true');
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
          panel.classList.remove('tabbed-collections__item--active');
        }
      });
    }

    if (setFocus) {
      focusElement(tab);
    }
  }

  function firstSelectedIndex(tabs, panels) {
    var i;

    for (i = 0; i < tabs.length; i += 1) {
      if (tabs[i].classList.contains('tabbed-collections__nav__button--active')) {
        return i;
      }
    }

    for (i = 0; i < panels.length; i += 1) {
      if (panels[i].classList.contains('tabbed-collections__item--active')) {
        return i;
      }
    }

    return 0;
  }

  function setupTabs(scope) {
    var root = scope || document;

    all('[data-section-type="tabbed-collections"], .tabbed-collections-section', root).forEach(function (section) {
      var tablist = section.querySelector('[data-tabs-nav]');
      if (!tablist) {
        return;
      }

      tablist.setAttribute('role', 'tablist');
      if (!tablist.hasAttribute('aria-label')) {
        tablist.setAttribute('aria-label', 'Featured product categories');
      }

      var tabs = all('[data-tab-button]', tablist);
      var panels = all('[data-tab-item]', section);
      var selectedIndex = firstSelectedIndex(tabs, panels);

      tabs.forEach(function (tab, index) {
        var panel = panels[index];
        if (!panel) {
          return;
        }

        if (!tab.id) {
          tab.id = 'TabbedCollectionsTab--' + (section.getAttribute('data-section-id') || 'section') + '-' + (index + 1);
        }
        if (!panel.id) {
          panel.id = 'TabbedCollectionsItem--' + (section.getAttribute('data-section-id') || 'section') + '-' + (index + 1);
        }

        var selected = index === selectedIndex;

        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        tab.classList.toggle('tabbed-collections__nav__button--active', selected);
        setTabPanelState(panel, tab, selected);
      });
    });
  }

  document.addEventListener('click', function (event) {
    var tab = closest(event.target, '[data-tab-button]');
    if (!tab) {
      return;
    }

    var tablist = closest(tab, '[role="tablist"], [data-tabs-nav]');
    if (!tablist) {
      return;
    }

    window.setTimeout(function () {
      activateTab(tab, false);
    }, 0);
  });

  document.addEventListener('keydown', function (event) {
    var tab = closest(event.target, '[data-tab-button][role="tab"]');
    if (!tab) {
      return;
    }

    var tablist = closest(tab, '[role="tablist"]');
    if (!tablist) {
      return;
    }

    var tabs = all('[data-tab-button][role="tab"]', tablist);
    var index = tabs.indexOf(tab);
    var nextIndex = index;

    if (index < 0 || tabs.length < 1) {
      return;
    }

    switch (event.key || event.keyCode) {
      case 'ArrowRight':
      case 'ArrowDown':
      case 39:
      case 40:
        nextIndex = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
      case 37:
      case 38:
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
      case 36:
        nextIndex = 0;
        break;
      case 'End':
      case 35:
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    activateTab(tabs[nextIndex], true);
  });

  function setupCarouselDots(scope) {
    var root = scope || document;

    all('.flickity-page-dots .dot', root).forEach(function (dot, index) {
      var selected = dot.classList.contains('is-selected') || dot.getAttribute('aria-current') === 'step' || dot.getAttribute('aria-selected') === 'true';

      if (dot.tagName.toLowerCase() !== 'button') {
        dot.setAttribute('role', 'button');
      }

      dot.setAttribute('tabindex', selected ? '0' : '-1');
      if (!dot.getAttribute('aria-label')) {
        dot.setAttribute('aria-label', 'Go to slide ' + (index + 1));
      }
      if (selected) {
        dot.setAttribute('aria-current', 'step');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  document.addEventListener('keydown', function (event) {
    var dot = closest(event.target, '.flickity-page-dots .dot[role="button"]');
    if (!dot) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ' || event.keyCode === 13 || event.keyCode === 32) {
      event.preventDefault();
      dot.click();
      window.setTimeout(function () {
        setupCarouselDots(document);
        focusElement(dot);
      }, 50);
    }
  });

  document.addEventListener('click', function (event) {
    var dot = closest(event.target, '.flickity-page-dots .dot');
    if (dot) {
      window.setTimeout(function () {
        setupCarouselDots(document);
      }, 50);
    }
  });

  function getOptionLabel(option) {
    var labelNode = option.querySelector('span');
    return (labelNode ? labelNode.textContent : option.textContent).trim();
  }

  function setToggleLabel(toggle, label) {
    if (!toggle || !label) {
      return;
    }

    var textTarget = toggle.querySelector('[data-popout-text]');
    var textNodes;

    if (textTarget) {
      textTarget.textContent = label;
      return;
    }

    textNodes = Array.prototype.slice.call(toggle.childNodes).filter(function (node) {
      return node.nodeType === 3 && node.textContent.trim();
    });

    if (textNodes[0]) {
      textNodes[0].textContent = ' ' + label + ' ';
    } else {
      toggle.insertBefore(document.createTextNode(label + ' '), toggle.firstChild);
    }
  }

  function interceptLocalizationAutoSubmit() {
    document.addEventListener('click', function (event) {
      var option = closest(event.target, '#LocalizationForm [data-popout-option]');
      if (!option) {
        return;
      }

      var form = closest(option, 'form');
      var popout = closest(option, '.popout');
      var list = popout ? popout.querySelector('[data-popout-list]') : null;
      var input = popout ? popout.querySelector('[data-popout-input]') : null;
      var toggle = popout ? popout.querySelector('[data-popout-toggle]') : null;
      var item = closest(option, '[data-popout-item], .popout-list__item');
      var value = option.getAttribute('data-value');
      var label = getOptionLabel(option);

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (input && value) {
        input.value = value;
        triggerChange(input);
      }

      if (list) {
        all('[aria-current="true"]', list).forEach(function (current) {
          current.removeAttribute('aria-current');
        });
        all('.popout-list__item--current', list).forEach(function (currentItem) {
          currentItem.classList.remove('popout-list__item--current');
        });
        option.setAttribute('aria-current', 'true');
        list.classList.remove('popout-list--visible');
      }

      if (item) {
        item.classList.add('popout-list__item--current');
      }

      if (toggle) {
        setToggleLabel(toggle, label);
        toggle.classList.remove('popout__toggle--expanded');
        toggle.setAttribute('aria-expanded', 'false');
      }

      if (form) {
        var submitButton = form.querySelector('.popout__submit');
        if (submitButton) {
          focusElement(submitButton);
        }
      }
    }, true);
  }


  function normalizeHomepageHeadings(scope) {
    var root = scope || document;

    if (!document.body || !document.body.classList.contains('template-index')) {
      return;
    }

    var headings = all('main h1', root);
    if (root !== document && headings.length === 0) {
      headings = all('main h1', document);
    }

    headings.forEach(function (heading, index) {
      if (index === 0) {
        return;
      }

      var replacement = document.createElement('h2');
      Array.prototype.slice.call(heading.attributes).forEach(function (attribute) {
        replacement.setAttribute(attribute.name, attribute.value);
      });
      while (heading.firstChild) {
        replacement.appendChild(heading.firstChild);
      }
      heading.parentNode.replaceChild(replacement, heading);
    });
  }

  function setupDecorativePaymentIcons(scope) {
    var root = scope || document;

    all('.payment-icon-list[aria-hidden="true"] .payment-icon', root).forEach(function (icon) {
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
      icon.setAttribute('role', 'presentation');
      icon.removeAttribute('aria-labelledby');
      all('title', icon).forEach(function (title) {
        title.parentNode.removeChild(title);
      });
    });
  }

  function observeDynamicScannerTargets() {
    if (!window.MutationObserver) {
      return;
    }

    var observer = new MutationObserver(function (mutations) {
      var needsDots = false;
      var needsIcons = false;

      mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
          needsDots = true;
          needsIcons = true;
        } else if (mutation.type === 'attributes' && mutation.attributeName === 'class' && matchesSelector(mutation.target, '.dot')) {
          needsDots = true;
        }
      });

      if (needsDots) {
        setupCarouselDots(document);
      }
      if (needsIcons) {
        setupDecorativePaymentIcons(document);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  ready(function () {
    watchDrawers();
    setupTabs(document);
    setupCarouselDots(document);
    setupDecorativePaymentIcons(document);
    normalizeHomepageHeadings(document);
    interceptLocalizationAutoSubmit();
    observeDynamicScannerTargets();

    document.addEventListener('focusin', function (event) {
      ensureFocusNotObscured(event.target);
    });

    document.addEventListener('shopify:section:load', function (event) {
      setupTabs(event.target);
      setupCarouselDots(event.target);
      setupDecorativePaymentIcons(event.target);
      normalizeHomepageHeadings(document);
      setDrawerState();
    });
  });
}());
